import { useMemo, useState } from "react";
import type {
  FileResponse,
  InventionElementResponse,
} from "@patent-studio/api-types";
import { useTask } from "@/hooks/use-tasks";
import type { DrawerDocument } from "../documents/DocumentDrawer";
import { highlightToneOf } from "./element-tags";

/** One empty list, so a task not yet read is the same value on every render. */
const NO_FILES: readonly FileResponse[] = [];

/**
 * The documents an element cites that are on the task, in the element's order,
 * each marked with the element's passages. A source naming no file, or a file
 * since removed, opens nothing; the same map says which citations are links.
 */
function citedDocuments(
  element: InventionElementResponse,
  onTask: ReadonlyMap<string, FileResponse>,
): DrawerDocument[] {
  const tone = highlightToneOf(element);
  return element.sources.flatMap((source) => {
    const file =
      source.file_id == null ? undefined : onTask.get(source.file_id);
    return file === undefined
      ? []
      : [{ file, marked: { elementId: element.element_id, tone } }];
  });
}

/**
 * Opening a cited source in the drawer: which documents an element cites that
 * the task still holds, and which one to open at.
 */
export function useSourceDrawer(taskId: string | null) {
  const files = useTask(taskId).data?.files ?? NO_FILES;
  const onTask = useMemo(
    () => new Map(files.map((f) => [f.file_id, f])),
    [files],
  );
  const [viewing, setViewing] = useState<{
    documents: DrawerDocument[];
    openAt: number;
  } | null>(null);
  return {
    onTask,
    viewing,
    open: (element: InventionElementResponse, fileId: string) => {
      const documents = citedDocuments(element, onTask);
      setViewing({
        documents,
        openAt: documents.findIndex((d) => d.file.file_id === fileId),
      });
    },
    close: () => {
      setViewing(null);
    },
  };
}
